/**
 * Global axios interceptor: retry idempotent requests on transient gateway failures
 * (502/503/504) and brief network drops — common during Render deploys / restarts.
 * Also one timeout retry for GET/HEAD/OPTIONS (ECONNABORTED).
 *
 * Import once at app bootstrap (index.js). apiService uses its own axios instance
 * and has matching retry logic in retryRequest.
 */
import axios from 'axios';

const MAX_RETRIES = 3;
const MAX_TIMEOUT_RETRIES = 1;
const BASE_DELAY_MS = 800;
const RETRY_STATUSES = new Set([502, 503, 504]);
const IDEMPOTENT = new Set(['get', 'head', 'options']);

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function retryDelayMs(error, attempt) {
  const header = error.response?.headers?.['retry-after'];
  if (header) {
    const asNum = Number(header);
    if (Number.isFinite(asNum) && asNum >= 0) return Math.min(asNum * 1000, 10_000);
  }
  return BASE_DELAY_MS * 2 ** (attempt - 1);
}

function shouldRetry(error, config) {
  if (!config || config.skipTransientRetry) return false;
  const method = String(config.method || 'get').toLowerCase();
  if (!IDEMPOTENT.has(method)) return false;

  const status = error.response?.status;
  if (status && RETRY_STATUSES.has(status)) return true;
  if (!error.response && (error.code === 'ERR_NETWORK' || error.message === 'Network Error')) {
    return true;
  }
  if (!error.response && error.code === 'ECONNABORTED') {
    return (config.__timeoutRetryCount || 0) < MAX_TIMEOUT_RETRIES;
  }
  return false;
}

let installed = false;

export function installAxiosTransientRetry() {
  if (installed) return;
  installed = true;

  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const config = error.config;
      if (!shouldRetry(error, config)) {
        return Promise.reject(error);
      }

      const isTimeout = error.code === 'ECONNABORTED';
      if (isTimeout) {
        config.__timeoutRetryCount = (config.__timeoutRetryCount || 0) + 1;
        await sleep(retryDelayMs(error, config.__timeoutRetryCount));
        return axios(config);
      }

      const attempt = (config.__transientRetryCount || 0) + 1;
      if (attempt > MAX_RETRIES) {
        return Promise.reject(error);
      }

      config.__transientRetryCount = attempt;
      await sleep(retryDelayMs(error, attempt));
      return axios(config);
    }
  );
}

export default installAxiosTransientRetry;
